'use client';

import { useState, useCallback, useEffect } from 'react';
import { useUser } from '@/context/UserContext';

const COOKIE_NAME = 'artes_turbo_tutorial_seen';
const COOKIE_MAX_AGE_DAYS = 365;

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * Hook to manage the tutorial modal state.
 * Shows the tutorial ONCE per cookie after user is authenticated.
 * After closing, triggers a tooltip hint.
 */
export function useTutorial() {
  const { user, isLoading } = useUser();
  const [showTutorial, setShowTutorial] = useState(false);
  const [showTooltipHint, setShowTooltipHint] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  // Check if tutorial should be shown once user data is loaded
  useEffect(() => {
    if (isLoading || hasChecked) return;
    if (!user) {
      // Not logged in, don't show
      setHasChecked(true);
      return;
    }

    const seen = getCookie(COOKIE_NAME);
    if (!seen) {
      // First time — show tutorial after a brief delay
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 800);
      setHasChecked(true);
      return () => clearTimeout(timer);
    }

    setHasChecked(true);
  }, [user, isLoading, hasChecked]);

  /** Close the modal and mark cookie. Also trigger tooltip hint. */
  const closeTutorial = useCallback(() => {
    setShowTutorial(false);
    setCookie(COOKIE_NAME, '1', COOKIE_MAX_AGE_DAYS);

    // Show tooltip hint after modal closes
    setTimeout(() => {
      setShowTooltipHint(true);
    }, 400);
  }, []);

  /** Manually re-open tutorial (e.g., from a button in the header) */
  const openTutorial = useCallback(() => {
    setShowTooltipHint(false);
    setShowTutorial(true);
  }, []);

  /** Dismiss the tooltip hint */
  const dismissTooltipHint = useCallback(() => {
    setShowTooltipHint(false);
  }, []);

  return {
    showTutorial,
    showTooltipHint,
    closeTutorial,
    openTutorial,
    dismissTooltipHint,
    isAuthenticated: Boolean(user),
  };
}
